import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    TextField,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    Chip
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload, X, Eye } from 'lucide-react';
import { getHomeTestimonials, createTestimonial, updateTestimonial, deleteTestimonial } from '../../Services/ContentHub/HomeTestmonialsApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const HomeTestimonials = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentTestimonial, setCurrentTestimonial] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        message: '',
        location: '',
        rating: '',
        testimonialImage: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedTestimonialId, setSelectedTestimonialId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // View modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedTestimonial, setSelectedTestimonial] = useState(null);

    // Fetch testimonials with useCallback to prevent multiple API calls
    const fetchTestimonials = useCallback(async (signal) => {
        try {
            setLoading(true);
            const response = await getHomeTestimonials({ signal });
            if (response && response.success) {
                setTestimonials(response.data);
            } else {
                showSnackbar("Failed to load testimonials.", "error");
            }
        } catch (err) {
            if (!signal?.aborted && err.name !== 'CanceledError') {
                showSnackbar("Failed to fetch testimonials. Please try again later.", "error");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        const controller = new AbortController();
        fetchTestimonials(controller.signal);
        return () => controller.abort();
    }, [fetchTestimonials]);

    const handleOpenModal = (testimonial = null, isEditMode = false) => {
        setFieldErrors({});
        if (testimonial) {
            setIsEdit(isEditMode);
            setCurrentTestimonial(testimonial);
            setFormData({
                name: testimonial.name || '',
                message: testimonial.message || '',
                location: testimonial.location || '',
                rating: testimonial.rating || '',
                testimonialImage: null
            });
            setImagePreview(testimonial.image);
        } else {
            setIsEdit(false);
            setCurrentTestimonial(null);
            setFormData({
                name: '',
                message: '',
                location: '',
                rating: '',
                testimonialImage: null
            });
            setImagePreview(null);
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentTestimonial(null);
        setFormData({
            name: '',
            message: '',
            location: '',
            rating: '',
            testimonialImage: null
        });
        setImagePreview(null);
        setFieldErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'message' && value.length > 200) return;
        if ((name === 'name' || name === 'location') && value.length > 100) return;

        let newValue = value;
        if (name === 'name' || name === 'location') {
            newValue = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 100);
        }

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({
                    ...prev,
                    testimonialImage: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed"
                }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            setFormData(prev => ({
                ...prev,
                testimonialImage: file
            }));
            if (fieldErrors.testimonialImage) {
                setFieldErrors(prev => ({
                    ...prev,
                    testimonialImage: null
                }));
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setFormData(prev => ({
            ...prev,
            testimonialImage: null
        }));
        setImagePreview(null);
        if (fieldErrors.testimonialImage) {
            setFieldErrors(prev => ({
                ...prev,
                testimonialImage: null
            }));
        }
    };

    const handleSubmit = async () => {
        const newFieldErrors = {};
        if (!formData.name || !formData.name.trim()) {
            newFieldErrors.name = "Name is required";
        } else if (formData.name.length > 100) {
            newFieldErrors.name = "Name must be at most 100 characters long";
        }

        if (!formData.location || !formData.location.trim()) {
            newFieldErrors.location = "Location is required";
        } else if (formData.location.length > 100) {
            newFieldErrors.location = "Location must be at most 100 characters long";
        }
        if (!formData.rating) newFieldErrors.rating = "Rating is required";
        if (!formData.message) {
            newFieldErrors.message = "Message is required";
        } else if (formData.message.length > 200) {
            newFieldErrors.message = "Message must be at most 200 characters long";
        }

        if (formData.testimonialImage && typeof formData.testimonialImage === 'object' && !formData.testimonialImage.type.startsWith("image/")) {
            newFieldErrors.testimonialImage = "Only image files are allowed";
        }

        if (!formData.testimonialImage && !isEdit) {
            newFieldErrors.testimonialImage = "Image is required";
        }

        if (Object.keys(newFieldErrors).length > 0) {
            setFieldErrors(newFieldErrors);
            return;
        }

        setSubmitting(true);
        setFieldErrors({});

        const data = new FormData();
        data.append('name', formData.name);
        data.append('message', formData.message);
        data.append('location', formData.location);
        data.append('rating', formData.rating);

        if (formData.testimonialImage) {
            data.append('testimonialImage', formData.testimonialImage);
        }

        try {
            if (isEdit) {
                await updateTestimonial(currentTestimonial.id, data);
                showSnackbar("Testimonial updated successfully!", "success");
            } else {
                await createTestimonial(data);
                showSnackbar("Testimonial created successfully!", "success");
            }

            handleCloseModal();
            // Refresh the list
            const controller = new AbortController();
            fetchTestimonials(controller.signal);
        } catch (err) {
            console.error(err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                `Failed to ${isEdit ? 'update' : 'create'} testimonial`;

            showSnackbar(errMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    errorsObj[item.field] = item.message;
                });
                setFieldErrors(errorsObj);
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                errorsObj['message'] = serverError.errors[0];
                setFieldErrors(errorsObj);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedTestimonialId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleOpenViewModal = (testimonial) => {
        setSelectedTestimonial(testimonial);
        setViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedTestimonial(null);
    };

    const handleConfirmDelete = async () => {
        if (selectedTestimonialId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteTestimonial(selectedTestimonialId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedTestimonialId(null);
                    showSnackbar("Testimonial deleted successfully!", "success");

                    // Refresh list
                    const controller = new AbortController();
                    fetchTestimonials(controller.signal);
                } else {
                    setDeleteError(response?.message || "Failed to delete testimonial");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete testimonial";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Testimonials
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',
                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                height: '40px',
                                textTransform: 'none',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Testimonial
                        </Button>
                    </PermissionWrapper>
                </Box>

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1100 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '60px' }}>Image</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Rating</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Message</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : testimonials.length > 0 ? (
                                    testimonials.map((testimonial, idx) => (
                                        <TableRow key={testimonial.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Avatar
                                                    src={testimonial.image}
                                                    alt={testimonial.name}
                                                    sx={{ width: 32, height: 32, border: '1px solid #EEE', borderRadius: '4px' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 550, color: '#333' }}>
                                                {testimonial.name}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{testimonial.location}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{testimonial.rating}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555', maxWidth: 300 }}>
                                                <Typography sx={{ fontSize: '0.8rem' }} noWrap title={testimonial.message}>{testimonial.message}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {testimonial.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {testimonial.uuName || '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="View">
                                                        <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                            size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenViewModal(testimonial)}>
                                                            <Eye size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(testimonial, true)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(testimonial.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No testimonials found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Testimonial' : 'Add Testimonial'}
                    </DialogTitle>
                    <DialogContent sx={{
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#FFBD65',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#F5F5F5',
                        }
                    }}>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Name *</Typography>
                                <TextField
                                    fullWidth
                                    name="name" size='small'
                                    placeholder="Enter name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    error={Boolean(fieldErrors.name)}
                                    helperText={fieldErrors.name}
                                    inputProps={{ maxLength: 100 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Location *</Typography>
                                <TextField
                                    fullWidth
                                    name="location" size='small'
                                    placeholder="Enter location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    error={Boolean(fieldErrors.location)}
                                    helperText={fieldErrors.location || "Note: Enter only city or state name"}
                                    FormHelperTextProps={{
                                        sx: { color: fieldErrors.location ? undefined : '#666', fontSize: '0.75rem' }
                                    }}
                                    inputProps={{ maxLength: 100 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Rating *</Typography>
                                <FormControl fullWidth error={Boolean(fieldErrors.rating)} size="small">
                                    <Select
                                        name="rating"
                                        value={formData.rating}
                                        onChange={handleInputChange}
                                        displayEmpty
                                        sx={{
                                            borderRadius: '6px',
                                            height: '40px',
                                            fontSize: '0.85rem',
                                            bgcolor: '#fff',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }}
                                    >
                                        <MenuItem value="" disabled sx={{ fontSize: '0.85rem' }}>Select Rating</MenuItem>
                                        <MenuItem value="1" sx={{ fontSize: '0.85rem' }}>1</MenuItem>
                                        <MenuItem value="1.5" sx={{ fontSize: '0.85rem' }}>1.5</MenuItem>
                                        <MenuItem value="2" sx={{ fontSize: '0.85rem' }}>2</MenuItem>
                                        <MenuItem value="2.5" sx={{ fontSize: '0.85rem' }}>2.5</MenuItem>
                                        <MenuItem value="3" sx={{ fontSize: '0.85rem' }}>3</MenuItem>
                                        <MenuItem value="3.5" sx={{ fontSize: '0.85rem' }}>3.5</MenuItem>
                                        <MenuItem value="4" sx={{ fontSize: '0.85rem' }}>4</MenuItem>
                                        <MenuItem value="4.5" sx={{ fontSize: '0.85rem' }}>4.5</MenuItem>
                                        <MenuItem value="5" sx={{ fontSize: '0.85rem' }}>5</MenuItem>
                                    </Select>
                                    {fieldErrors.rating && (
                                        <FormHelperText sx={{ fontSize: '0.75rem' }}>{fieldErrors.rating}</FormHelperText>
                                    )}
                                </FormControl>
                            </Box>

                            <Box>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography sx={{ fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Message *</Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: formData.message.length >= 200 ? 'error.main' : 'text.secondary' }}>
                                        {formData.message.length}/200
                                    </Typography>
                                </Stack>
                                <TextField
                                    fullWidth
                                    name="message"
                                    placeholder="Enter testimonial message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                    error={Boolean(fieldErrors.message)}
                                    helperText={fieldErrors.message}
                                    inputProps={{ maxLength: 200 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Profile Image *</Typography>
                                <Box
                                    sx={{
                                        border: fieldErrors.testimonialImage ? '2px dashed #FF4D49' : '2px dashed #E0E0E0',
                                        borderRadius: '6px',
                                        p: 2,
                                        textAlign: 'center',
                                        bgcolor: fieldErrors.testimonialImage ? '#FFF5F5' : '#FAFAFA',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        '&:hover': {
                                            borderColor: fieldErrors.testimonialImage ? '#FF4D49' : '#FFBD65',
                                            bgcolor: fieldErrors.testimonialImage ? '#FFF5F5' : '#FFF8ED'
                                        }
                                    }}
                                >
                                    {imagePreview ? (
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                style={{ maxHeight: 120, maxWidth: '100%', borderRadius: '4px' }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={handleRemoveImage}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -10,
                                                    right: -10,
                                                    bgcolor: '#FFFFFF',
                                                    color: '#EF4444',
                                                    boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
                                                    '&:hover': {
                                                        bgcolor: '#FEE2E2',
                                                        color: '#DC2626'
                                                    }
                                                }}
                                            >
                                                <X size={14} color="#EF4444" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <label style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}>
                                            <input
                                                type="file"
                                                accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml"
                                                hidden
                                                onChange={handleImageChange}
                                            />
                                            <Stack spacing={1} alignItems="center">
                                                <Box sx={{ p: 1, borderRadius: '50%', bgcolor: fieldErrors.testimonialImage ? '#FFEBEB' : '#FFF8ED', color: fieldErrors.testimonialImage ? '#FF4D49' : '#FFBD65', display: 'inline-flex' }}>
                                                    <Upload size={18} />
                                                </Box>
                                                <Typography sx={{ fontSize: '0.8rem', color: fieldErrors.testimonialImage ? '#FF4D49' : '#666' }}>
                                                    {fieldErrors.testimonialImage || 'Click to upload image'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#999' }}>PNG, JPG or WebP (max. 3MB)</Typography>
                                            </Stack>
                                        </label>
                                    )}
                                </Box>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button
                            onClick={handleCloseModal}
                            sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                color: '#222',
                                px: 2.5,
                                fontSize: '0.85rem'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Details Modal */}
                <Dialog open={viewModalOpen} onClose={handleCloseViewModal} maxWidth="xl" fullWidth
                    PaperProps={{ sx: { borderRadius: '10px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxWidth: '1150px' } }}
                >
                    {/* Header */}
                    <Box sx={{ px: 3, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E0E0E0' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '1.1rem', lineHeight: 1.2 }}>
                                Testimonial Details
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mt: 0.3 }}>
                                Complete information for the selected testimonial
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={handleCloseViewModal} sx={{ color: '#64748B' }} disableFocusRipple disableRipple disableTouchRipple>
                            <X size={20} />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 0, bgcolor: '#FFFFFF', maxHeight: '70vh', overflowY: 'auto' }}>
                        {selectedTestimonial && (
                            <Box sx={{ p: 3 }}>
                                {/* Summary Cards Row */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 0, mb: 3, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden', bgcolor: '#F5F5F5' }}>
                                    {/* Card 1 */}
                                    <Box sx={{ p: 2, borderRight: { xs: 'none', sm: '1px solid #E0E0E0' }, borderBottom: { xs: '1px solid #E0E0E0', sm: 'none' }, bgcolor: '#FFFFFF' }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500, textTransform: 'uppercase', mb: 1 }}>
                                            Testimonial ID
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1E293B' }}>
                                            #{selectedTestimonial.id}
                                        </Typography>
                                    </Box>

                                    {/* Card 2 */}
                                    <Box sx={{ p: 2, borderRight: { xs: 'none', md: '1px solid #E0E0E0' }, borderBottom: { xs: '1px solid #E0E0E0', sm: 'none' }, bgcolor: '#FFFFFF' }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500, textTransform: 'uppercase', mb: 1 }}>
                                            Rating
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#2E7D32' }}>
                                            {selectedTestimonial.rating}/5
                                        </Typography>
                                    </Box>

                                    {/* Card 3 */}
                                    <Box sx={{ p: 2, borderRight: { xs: 'none', md: '1px solid #E0E0E0' }, borderBottom: { xs: '1px solid #E0E0E0', md: 'none' }, bgcolor: '#FFFFFF' }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500, textTransform: 'uppercase', mb: 1 }}>
                                            Name
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1E293B' }}>
                                            {selectedTestimonial.name}
                                        </Typography>
                                    </Box>

                                    {/* Card 4 */}
                                    <Box sx={{ p: 2, bgcolor: '#FFFFFF' }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500, textTransform: 'uppercase', mb: 1 }}>
                                            Location
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1E293B' }}>
                                            {selectedTestimonial.location}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Testimonial Information Section */}
                                <Box sx={{ mb: 3, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#E3F2FD', borderBottom: '1px solid #BBDEFB' }}>
                                        <Eye size={18} color="#1565C0" />
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1565C0' }}>
                                            Testimonial Information
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
                                            {/* Left Column */}
                                            <Box sx={{ pr: { md: 2 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Name
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400 }}>
                                                        {selectedTestimonial.name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Location
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400 }}>
                                                        {selectedTestimonial.location}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Rating
                                                    </Typography>
                                                    <Chip
                                                        label={`${selectedTestimonial.rating}/5`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#E8F5E9',
                                                            color: '#2E7D32',
                                                            border: '1px solid #C8E6C9',
                                                            fontWeight: 500,
                                                            fontSize: '0.7rem',
                                                            borderRadius: '4px',
                                                            height: 22
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            {/* Right Column */}
                                            <Box sx={{ pl: { md: 2 } }}>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Message
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400, wordBreak: 'break-word' }}>
                                                        {selectedTestimonial.message}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Profile Image
                                                    </Typography>
                                                    {selectedTestimonial.image && (
                                                        <img
                                                            src={selectedTestimonial.image}
                                                            alt="Profile"
                                                            style={{ width: 80, height: 80, borderRadius: '8px', objectFit: 'cover', border: '1px solid #E0E0E0' }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Audit Information Section */}
                                <Box sx={{ mb: 2, border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#F5F5F5', borderBottom: '1px solid #E0E0E0' }}>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#616161' }}>
                                            Audit Information
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
                                            {/* Left Column */}
                                            <Box sx={{ pr: { md: 2 }, borderRight: { xs: 'none', md: '1px solid #E0E0E0' } }}>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Created By
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400 }}>
                                                        {selectedTestimonial.cuName || '-'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Created At
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400 }}>
                                                        {formatDate(selectedTestimonial.createdAt)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {/* Right Column */}
                                            <Box sx={{ pl: { md: 2 } }}>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Updated By
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400 }}>
                                                        {selectedTestimonial.uuName || '-'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500, mb: 0.4 }}>
                                                        Updated At
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 400 }}>
                                                        {formatDate(selectedTestimonial.updatedAt)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #E0E0E0', bgcolor: '#FFFFFF' }}>
                        <Button
                            disableElevation
                            onClick={handleCloseViewModal}
                            variant="outlined"
                            sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                color: '#1E293B',
                                borderColor: '#E0E0E0',
                                px: 3,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Testimonial"
                    description="Are you sure you want to delete this testimonial? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default HomeTestimonials;
